import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";

/**
 * Reusable image uploader. Uploads to /api/upload (Emergent object storage)
 * and reports back the relative path (e.g. "/api/files/...") via onUploaded.
 */
export default function ImageUpload({ currentUrl, onUploaded, placeholderUrl, testId, shape = "circle", mediaType = "image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      onUploaded(data.url);
    } catch (err) {
      toast({
        title: mediaType === "video" ? "Error al subir el video" : "Error al subir la imagen",
        description: formatApiErrorDetail(err.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const displayUrl = currentUrl ? getFileUrl(currentUrl) : placeholderUrl;
  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-xl";
  const acceptTypes = mediaType === "video" ? "video/mp4,video/quicktime,video/webm" : "image/png,image/jpeg,image/jpg,image/gif,image/webp,image/avif";

  return (
    <div className="flex items-center gap-4">
      <div className={`w-16 h-16 ${shapeClass} overflow-hidden border border-border bg-muted shrink-0`}>
        {displayUrl && mediaType === "video" && (
          <video src={displayUrl} className="w-full h-full object-cover" muted data-testid={`${testId}-preview`} />
        )}
        {displayUrl && mediaType !== "video" && (
          <img src={displayUrl} alt="preview" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={acceptTypes}
        className="hidden"
        onChange={handleFile}
        data-testid={`${testId}-file-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`${testId}-upload-button`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        {uploading ? "Subiendo..." : (mediaType === "video" ? "Subir video" : "Cambiar foto")}
      </Button>
    </div>
  );
}
